'use client';

import { ForwardedRef, forwardRef, useImperativeHandle, useState } from 'react';
import { useIntl } from 'react-intl';
import { usePrivate } from '@/components/private';
import { enqueueSnackbar } from '@/providers';
import { slate } from '@/lib/colours';
import { PrefixSettingTypes, PrefixSettings } from '@/lib/prefixes';
import { ResponseValidationErrors } from '@/lib/types';
import {
  Box,
  CircularProgress,
  Switch,
  TextField,
  Typography,
  type SwitchProps,
  type TextFieldProps,
} from '@/primitives';
import { prefixConfigCardMessages } from './messages';
import { useSortable } from '@dnd-kit/sortable';
import { DragIndicator } from '@mui/icons-material';
import { CSS } from '@dnd-kit/utilities';
import { Z_INDEX } from '@/configs/layout';

export interface PrefixConfigCardAPI {
  validate: () => void;
}
interface Props {
  description?: string;
  disabled?: boolean;
  label: string;
  mode: 'add' | 'config' | 'edit';
  name: string;
  placeholder?: string;
  prefixSettings: PrefixSettings;
  title?: string;
  validationErrors: ResponseValidationErrors;
  onChangeEnabled?: (enabled: boolean) => void;
  onChangeFieldValue: (value: string) => void;
  onChangeLimit?: (limit: number) => void;
  setIsFormDirty: (isDirty: boolean) => void;
  setValidationErrors: (
    errors:
      | ResponseValidationErrors
      | ((prevErrors: ResponseValidationErrors) => ResponseValidationErrors),
  ) => void;
}

export const PrefixConfigCard = forwardRef(function PrefixConfigCard(
  {
    description,
    disabled = false,
    label,
    mode,
    name,
    placeholder,
    prefixSettings,
    title,
    validationErrors,
    onChangeEnabled,
    onChangeFieldValue,
    onChangeLimit,
    setIsFormDirty,
    setValidationErrors,
  }: Props,
  ref: ForwardedRef<PrefixConfigCardAPI>,
) {
  const { enterprisePrefixApi, selectedOrganisationId } = usePrivate();
  const intl = useIntl();

  const [isValidating, setIsValidating] = useState(false);
  const [limitError, setLimitError] = useState('');

  const {
    attributes,
    isDragging,
    listeners,
    transform,
    transition,
    setNodeRef,
    setActivatorNodeRef,
  } = useSortable({ id: prefixSettings.order });

  const style = {
    border: isDragging ? `1px solid ${slate[500]}` : 'none',
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? Z_INDEX.modal : undefined,
  };

  const handleChangeEnabled: SwitchProps['onChange'] = (event) => {
    setIsFormDirty(true);
    const checked = event.target.checked;
    if (onChangeEnabled) {
      onChangeEnabled(checked);
    }
  };

  const handleServerValidation = (value: string) => {
    setIsValidating(true);

    enterprisePrefixApi
      ?.apiEnterprisePrefixCheckValidPrefixForOrganisationPost({
        prefixType: prefixSettings.settingTypeCode as PrefixSettingTypes,
        prefix: value,
      })
      .then((response) => {
        // true when valid prefix
        // false when prefix is taken
        if (!response.data && mode !== 'config') {
          setValidationErrors({
            message: [
              intl.formatMessage(
                prefixConfigCardMessages[
                  'prefixConfigCard.validationMessage.prefixTaken'
                ].props,
              ),
            ],
          });
        }
      })
      .catch((error) => {
        enqueueSnackbar('Server validation error', { variant: 'error' });
        console.error('Server validation error:', error);
      })
      .finally(() => {
        setIsValidating(false);
      });
  };

  const handleFieldValidation = (value: string) => {
    const trimmed = value.trim();

    if (!trimmed) {
      setValidationErrors({
        message: [
          intl.formatMessage(
            prefixConfigCardMessages[
              'prefixConfigCard.validationMessage.prefixBlank'
            ].props,
          ),
        ],
      });
      return false;
    }

    setValidationErrors({});

    if (mode !== 'config') {
      handleServerValidation(trimmed);
    }

    return true;
  };

  const handleChangeFieldValue: TextFieldProps['onChange'] = (event) => {
    setIsFormDirty(true);
    const value = event.target.value;
    onChangeFieldValue(value);
  };

  const handleChangeLimit: TextFieldProps['onChange'] = (event) => {
    if (!onChangeLimit) return;

    setIsFormDirty(true);
    const value = parseInt(event.target.value, 10);

    if (!value) {
      setLimitError('Please enter a valid limit.');
    } else {
      setLimitError('');
      onChangeLimit(value);
    }
  };

  useImperativeHandle(ref, () => ({
    validate: () => {
      handleFieldValidation(prefixSettings.value);
    },
  }));

  return (
    <div ref={setNodeRef} style={style}>
      <Box
        sx={{
          display: 'flex',
          gap: '16px',
          px: '16px',
          py: '8px',
          backgroundColor: mode === 'config' ? slate[50] : 'transparent',
          borderRadius: '8px',
          width: '100%',
        }}
      >
        {mode === 'config' && (
          <div
            ref={setActivatorNodeRef}
            style={{ display: 'flex', alignItems: 'center' }}
            {...attributes}
            {...listeners}
          >
            <DragIndicator style={{ cursor: 'grab' }} />
          </div>
        )}
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}
        >
          {mode === 'config' && title && (
            <Box>
              <Typography variant="titlemedium">{title}</Typography>
              {description && (
                <Typography variant="bodymedium" sx={{ color: slate[600] }}>
                  {description}
                </Typography>
              )}
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: '16px', flexDirection: 'row' }}>
            <TextField
              disabled={disabled}
              error={!!validationErrors.message && !prefixSettings.value.trim()}
              helperText={validationErrors.message}
              InputProps={{
                endAdornment: isValidating ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null,
              }}
              inputProps={
                mode !== 'config'
                  ? { maxLength: prefixSettings.characterLimit }
                  : undefined
              }
              label={label}
              name={name}
              placeholder={placeholder}
              required
              sx={{ width: '300px' }}
              value={prefixSettings.value}
              variant="filled"
              onBlur={(e) => handleFieldValidation(e.target.value)}
              onChange={handleChangeFieldValue}
            />
            {mode === 'config' && onChangeLimit && (
              <Box>
                <TextField
                  error={!!limitError}
                  helperText={limitError}
                  inputProps={{ min: 1 }}
                  label={
                    prefixConfigCardMessages[
                      'prefixConfigCard.limitField.label'
                    ]
                  }
                  name="limit"
                  placeholder={intl.formatMessage(
                    prefixConfigCardMessages[
                      'prefixConfigCard.limitField.placeholder'
                    ].props,
                  )}
                  required
                  sx={{ width: '120px' }}
                  type="number"
                  value={
                    prefixSettings.characterLimit
                      ? prefixSettings.characterLimit
                      : ''
                  }
                  variant="filled"
                  onChange={handleChangeLimit}
                />
              </Box>
            )}
          </Box>
        </Box>
        {mode === 'config' && (
          <Box>
            <Switch
              checked={prefixSettings.requiredByDefault}
              onChange={handleChangeEnabled}
            />
          </Box>
        )}
      </Box>
    </div>
  );
});
